import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowLeftIcon,
  PlusIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";

// ---------------------------------------------------------------------------
// AlertDialog — reused from PieceWorkerLogTab
// ---------------------------------------------------------------------------
const AlertDialog = ({
  type = "error",
  title = "Alert",
  message = "Something happened.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  open = false,
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const styles = {
    error: {
      icon: <ExclamationTriangleIcon className="size-6 text-red-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-red-400 hover:bg-red-500",
    },
    success: {
      icon: <CheckCircleIcon className="size-6 text-blue-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-blue-500 hover:bg-blue-600",
    },
  };

  const { icon, iconBg, buttonBg } = styles[type] || styles.error;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-600/75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-gray-700 px-4 pb-4 pt-5 shadow-xl transition-all sm:max-w-lg sm:p-6 border border-gray-600">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex size-10 shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0`}
              >
                {icon}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-lg font-semibold text-blue-400"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-200">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex w-full justify-center rounded-md ${buttonBg} px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm sm:w-auto transition-colors duration-200`}
              >
                {confirmText}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm ring-1 ring-gray-500 hover:bg-gray-500 sm:w-auto transition-colors duration-200"
              >
                {cancelText}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Availability colour helper
// ---------------------------------------------------------------------------
const getAvailabilityColor = (estimated, installed, pending) => {
  const totalUsed = (installed || 0) + (pending || 0);
  const available = (estimated || 0) - totalUsed;
  const percentage = estimated > 0 ? (available / estimated) * 100 : 0;
  if (percentage >= 60)
    return { bg: "bg-green-400", text: "text-green-400", percentage };
  if (percentage >= 20)
    return { bg: "bg-yellow-400", text: "text-yellow-400", percentage };
  return { bg: "bg-red-400", text: "text-red-400", percentage };
};

// ---------------------------------------------------------------------------
// ProgressLogTab
// ---------------------------------------------------------------------------
const ProgressLogTab = ({
  project,
  activeStatusTab,
  setActiveStatusTab,
  onViewLog,
}) => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const API = "https://erp-xe9u.onrender.com/api";

  // ── History state ──
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(activeStatusTab || "Pending");

  // ── Create flow state ──
  const [mode, setMode] = useState("history"); // "history" | "create"
  const [stage, setStage] = useState("workOrder"); // "workOrder" | "lineItems"
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [notes, setNotes] = useState("");
  const [workCompletedDate, setWorkCompletedDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  // ── Filters ──
  const [bidAreaFilter, setBidAreaFilter] = useState("");
  const [bidAreas, setBidAreas] = useState([]);

  // ── UI feedback ──
  const [createError, setCreateError] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Keep parent in sync with active tab
  useEffect(() => {
    if (setActiveStatusTab) setActiveStatusTab(activeTab);
  }, [activeTab, setActiveStatusTab]);

  // ---------------------------------------------------------------------------
  // Fetch progress logs for the project
  // ---------------------------------------------------------------------------
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const response = await axios.get(
        `${API}/progressLogs?project=${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const logsData = response.data.data || response.data || [];
      setLogs(logsData);
      setFilteredLogs(logsData.filter((l) => l.status === "pending"));
    } catch (err) {
      console.error("Error fetching progress logs:", err);
      setError("Failed to load progress logs.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Keep filtered list in sync with active tab
  useEffect(() => {
    const statusMap = {
      Pending: "pending",
      Approved: "approved",
      Rejected: "rejected",
    };
    setFilteredLogs(logs.filter((l) => l.status === statusMap[activeTab]));
  }, [activeTab, logs]);

  // ---------------------------------------------------------------------------
  // Fetch hourly work orders for this project
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchWorkOrders = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API}/workorders2/project/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const hourlyOrders = (response.data.workOrders || []).filter(
          (wo) => wo.type === "hourly",
        );
        setWorkOrders(hourlyOrders);
      } catch (err) {
        console.error("Error fetching work orders:", err);
      }
    };
    if (projectId) fetchWorkOrders();
  }, [projectId]);

  // ---------------------------------------------------------------------------
  // Select work order → load available labour items
  // ---------------------------------------------------------------------------
  const handleSelectWorkOrder = async (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setCreateLoading(true);
    setCreateError(null);

    try {
      const token = localStorage.getItem("token");

      const itemsResponse = await axios.get(
        `${API}/progressLogs/work-order/${workOrder._id}/available-items`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const items = itemsResponse.data.items || [];
      setAvailableItems(items);

      const uniqueBidAreas = [
        ...new Set(items.map((item) => item.bidArea).filter(Boolean)),
      ];
      setBidAreas(uniqueBidAreas);
      setBidAreaFilter("");

      const initialQuantities = {};
      items.forEach((item) => {
        initialQuantities[item._id] = "";
      });
      setQuantities(initialQuantities);

      setStage("lineItems");
    } catch (err) {
      console.error("Error loading work order data:", err);
      setCreateError("Failed to load work order data.");
    } finally {
      setCreateLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Quantity validation
  // ---------------------------------------------------------------------------
  const handleQuantityChange = (itemId, value) => {
    const numValue = parseFloat(value) || 0;
    const item = availableItems.find((li) => li._id === itemId);

    if (item && numValue > item.qtyRemaining) {
      setCreateError(
        `Quantity cannot exceed remaining ${item.qtyRemaining} ${item.uom} for "${item.title}"`,
      );
      setQuantities((prev) => ({
        ...prev,
        [itemId]: item.qtyRemaining.toString(),
      }));
    } else {
      setCreateError(null);
      setQuantities((prev) => ({ ...prev, [itemId]: value }));
    }
  };

  // ---------------------------------------------------------------------------
  // Submit new progress log
  // ---------------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!workCompletedDate) {
      setCreateError("Please enter a work completed date.");
      return;
    }

    const lineItemsToSubmit = availableItems
      .filter((item) => parseFloat(quantities[item._id] || 0) > 0)
      .map((item) => ({
        lineItemId: item._id,
        assemblyId: item.assemblyId,
        quantity: parseFloat(quantities[item._id]),
        title: item.title,
        costCodeDescription: item.costCodeDescription || item.title,
        uom: item.uom,
        unitCost: item.unitCost,
        bidArea: item.bidArea || "",
        conditionName: item.conditionName || "",
        sectionNumber: item.sectionNumber || "",
      }));

    if (lineItemsToSubmit.length === 0) {
      setCreateError(
        "At least one line item with a quantity greater than 0 is required.",
      );
      return;
    }

    try {
      setCreateLoading(true);
      const token = localStorage.getItem("token");

      const payload = {
        projectId,
        workOrderId: selectedWorkOrder._id,
        lineItems: lineItemsToSubmit,
        workCompletedDate,
        notes,
      };

      const response = await axios.post(`${API}/progressLogs`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setLogs((prev) => [response.data.data, ...prev]);
      setActiveTab("Pending");
      setMode("history");

      setDialog({
        open: true,
        type: "success",
        title: "Progress Log Submitted",
        message: "The progress log has been submitted for review.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });

      resetForm();
    } catch (err) {
      console.error("Error submitting progress log:", err);
      const errorMsg =
        err.response?.data?.message || "Failed to submit progress log.";
      setCreateError(errorMsg);
      setDialog({
        open: true,
        type: "error",
        title: "Submission Failed",
        message: errorMsg,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setCreateLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Reset create flow
  // ---------------------------------------------------------------------------
  const resetForm = () => {
    setSelectedWorkOrder(null);
    setAvailableItems([]);
    setQuantities({});
    setNotes("");
    setWorkCompletedDate(new Date().toISOString().split("T")[0]);
    setStage("workOrder");
    setCreateError(null);
    setBidAreaFilter("");
    setBidAreas([]);
  };

  // ---------------------------------------------------------------------------
  // Navigate to log detail view
  // ---------------------------------------------------------------------------
  const handleViewLog = (logId) => {
    if (onViewLog) {
      onViewLog(logId);
    } else {
      navigate(`/progress-logs/${logId}`, {
        state: { fromProject: true, projectId, activeStatusTab: activeTab },
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------
  const filteredItems = availableItems.filter(
    (item) => !bidAreaFilter || item.bidArea === bidAreaFilter,
  );

  const totalQtyEntered = availableItems.reduce(
    (sum, item) => sum + (parseFloat(quantities[item._id] || 0) || 0),
    0,
  );

  // Progress percentage across all items (estimated vs remaining)
  const overallProgress = (() => {
    const totalEst = availableItems.reduce(
      (s, i) => s + (i.qtyEstimated || 0),
      0,
    );
    const totalApproved = availableItems.reduce(
      (s, i) => s + (i.qtyInstalled || i.qtyApproved || 0),
      0,
    );
    const totalPending = availableItems.reduce(
      (s, i) => s + (i.qtyPending || 0),
      0,
    );
    if (totalEst === 0) return 0;
    return Math.min(100, ((totalApproved + totalPending) / totalEst) * 100);
  })();

  const tabs = ["Pending", "Approved", "Rejected"];

  // ---------------------------------------------------------------------------
  // Loading / error guards
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-gray-200 text-lg">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-red-400 text-lg">{error}</div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600">
      <div className="mx-auto px-4 py-6">
        {/* ── HISTORY MODE ── */}
        {mode === "history" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Progress Logs
              </h2>
              <button
                onClick={() => setMode("create")}
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-gray-200 text-sm font-semibold rounded-md hover:bg-blue-600 transition-colors duration-200 shadow-sm"
              >
                <PlusIcon className="size-4 mr-2" />
                New Progress Report
              </button>
            </div>

            {/* Status tabs */}
            <div className="border-b border-gray-600 mb-4">
              <nav className="flex space-x-4">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-2 px-4 text-sm font-medium rounded-t-md transition-colors duration-200 ${
                      activeTab === tab
                        ? "bg-gray-600 text-blue-400 border-b-2 border-blue-400"
                        : "text-gray-400 hover:text-gray-200 hover:bg-gray-600"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            {/* Logs table */}
            <div className="overflow-hidden rounded-lg shadow-xl">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Work Order
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Submitted By
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Work Date
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Total Qty
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Notes
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {filteredLogs.length > 0 ? (
                    filteredLogs.map((log, index) => (
                      <tr
                        key={log._id}
                        onClick={() => handleViewLog(log._id)}
                        className={`${
                          index % 2 === 0 ? "bg-gray-700" : "bg-gray-600"
                        } hover:bg-gray-500 cursor-pointer transition-colors duration-200`}
                      >
                        <td className="px-4 py-2 text-sm text-gray-200">
                          <div>{log.workOrder?.title || "N/A"}</div>
                          <div className="text-xs text-gray-400">
                            {log.woNumber}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-200">
                          {log.user?.displayName || log.user?.email || "N/A"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-200">
                          {log.workCompletedDate
                            ? new Date(
                                log.workCompletedDate,
                              ).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-200">
                          {(log.totalQtyInstalled || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-400 max-w-xs truncate">
                          {log.notes || "—"}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLog(log._id);
                            }}
                            className="text-blue-400 hover:text-blue-300 transition-colors duration-200"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-4 py-4 text-center text-sm text-gray-400"
                      >
                        {logs.length === 0
                          ? "No progress logs yet."
                          : `No ${activeTab.toLowerCase()} logs found.`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── CREATE MODE ── */}
        {mode === "create" && (
          <div className="bg-gray-700 rounded-lg">
            {createError && (
              <div className="bg-red-400/10 border border-red-400 text-red-400 px-4 py-3 rounded-md mb-4">
                {createError}
              </div>
            )}

            {createLoading && (
              <div className="text-center py-4 text-gray-200">Loading...</div>
            )}

            {/* STEP 1: Select work order */}
            {stage === "workOrder" && !createLoading && (
              <>
                <h2 className="text-lg font-semibold text-blue-400 mb-4">
                  Select Hourly Work Order
                </h2>

                {workOrders.length > 0 ? (
                  <div className="grid gap-3">
                    {workOrders.map((wo) => (
                      <div
                        key={wo._id}
                        onClick={() => handleSelectWorkOrder(wo)}
                        className="flex items-center bg-gray-600 p-4 rounded-md cursor-pointer hover:bg-gray-500 transition-colors duration-200"
                      >
                        <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                          <ClipboardDocumentListIcon className="w-5 h-5 text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-200">
                            {wo.title}
                          </div>
                          <div className="text-xs text-gray-400">
                            WO# {wo.woNumber}
                          </div>
                        </div>
                        <span className="text-xs text-blue-400 font-medium bg-blue-400/10 px-2 py-1 rounded">
                          Hourly
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">
                    No hourly work orders available for this project.
                  </p>
                )}

                <button
                  onClick={() => setMode("history")}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  <ArrowLeftIcon className="size-4 mr-2" />
                  Back to Logs
                </button>
              </>
            )}

            {/* STEP 2: Enter quantities */}
            {stage === "lineItems" && !createLoading && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-white">
                      {selectedWorkOrder?.title}
                    </h2>
                    <p className="text-sm text-gray-400">
                      WO# {selectedWorkOrder?.woNumber} — Report completed
                      quantities
                    </p>
                  </div>

                  {/* Overall progress pill */}
                  <div className="flex flex-col items-end">
                    <span className="text-xs text-gray-400 mb-1">
                      Overall progress
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-500 rounded-full h-2">
                        <div
                          className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${Math.min(overallProgress, 100)}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-blue-400 font-medium">
                        {overallProgress.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Date + bid area filter row */}
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Work Completed Date
                    </label>
                    <input
                      type="date"
                      value={workCompletedDate}
                      onChange={(e) => setWorkCompletedDate(e.target.value)}
                      className="bg-gray-600 border border-gray-500 rounded-md px-3 py-2 text-sm text-gray-200 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {bidAreas.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Filter by Bid Area
                      </label>
                      <select
                        value={bidAreaFilter}
                        onChange={(e) => setBidAreaFilter(e.target.value)}
                        className="bg-gray-600 border border-gray-500 rounded-md px-3 py-2 text-sm text-gray-200 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">All Bid Areas</option>
                        {bidAreas.map((bidArea) => (
                          <option key={bidArea} value={bidArea}>
                            {bidArea}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Line items */}
                {filteredItems.length > 0 ? (
                  <div className="space-y-4 mb-6">
                    {filteredItems.map((item) => {
                      const { bg, text, percentage } = getAvailabilityColor(
                        item.qtyEstimated,
                        item.qtyInstalled || item.qtyApproved,
                        item.qtyPending,
                      );

                      return (
                        <div
                          key={item._id}
                          className="bg-gray-600 p-4 rounded-md"
                        >
                          {/* Item header */}
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h3 className="text-gray-200 font-medium text-sm">
                                {item.title}
                              </h3>
                              {item.conditionName && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  Assembly: {item.conditionName}
                                </p>
                              )}
                              {item.bidArea && (
                                <p className="text-xs text-gray-400">
                                  Bid Area: {item.bidArea}
                                </p>
                              )}
                            </div>
                            {item.hasVariance && (
                              <span className="inline-block px-2 py-0.5 text-xs bg-yellow-400/20 text-yellow-400 rounded ml-2 shrink-0">
                                VARIANCE
                              </span>
                            )}
                          </div>

                          {/* Qty stats */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2 text-sm">
                            <p className="text-gray-200">
                              Budget:{" "}
                              {(item.qtyEstimated || 0).toLocaleString()}{" "}
                              {item.uom}
                            </p>
                            <p className="text-gray-200">
                              Approved:{" "}
                              {(
                                item.qtyInstalled ||
                                item.qtyApproved ||
                                0
                              ).toLocaleString()}{" "}
                              {item.uom}
                            </p>
                            <p className="text-gray-200">
                              Pending: {(item.qtyPending || 0).toLocaleString()}{" "}
                              {item.uom}
                            </p>
                            <p className={text}>
                              Available:{" "}
                              {(item.qtyRemaining || 0).toLocaleString()}{" "}
                              {item.uom} ({percentage.toFixed(1)}%)
                            </p>
                          </div>

                          {/* Availability bar */}
                          <div className="w-full bg-gray-500 rounded-full h-1.5 mb-3">
                            <div
                              className={`${bg} h-1.5 rounded-full transition-all duration-300`}
                              style={{ width: `${Math.min(percentage, 100)}%` }}
                            />
                          </div>

                          {/* Quantity input */}
                          <div className="flex items-center gap-3">
                            <input
                              type="number"
                              value={quantities[item._id] || ""}
                              onChange={(e) =>
                                handleQuantityChange(item._id, e.target.value)
                              }
                              placeholder="Qty installed"
                              min="0"
                              max={item.qtyRemaining}
                              disabled={item.qtyRemaining <= 0}
                              className="bg-gray-700 border border-gray-500 rounded-md p-2 w-32 text-sm text-gray-200 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <span className="text-sm text-gray-400">
                              {item.uom}
                            </span>
                            {item.qtyRemaining <= 0 && (
                              <span className="text-xs text-red-400">
                                Fully charged
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-gray-600 rounded-md p-6 text-center mb-6">
                    <p className="text-sm text-gray-400">
                      {availableItems.length === 0
                        ? "No labour items available for this work order."
                        : "No items match the selected bid area filter."}
                    </p>
                  </div>
                )}

                {/* Notes */}
                <div className="mb-6">
                  <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                    Notes (optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any relevant notes about this progress report..."
                    rows={3}
                    className="w-full bg-gray-600 border border-gray-500 rounded-md px-3 py-2 text-sm text-gray-200 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>

                {/* Total qty entered */}
                {totalQtyEntered > 0 && (
                  <div className="mb-4 p-4 bg-blue-400/10 border border-blue-400/30 rounded-md">
                    <p className="text-sm font-semibold text-blue-400">
                      Total qty to report: {totalQtyEntered.toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex space-x-4">
                  <button
                    onClick={handleSubmit}
                    disabled={createLoading || totalQtyEntered === 0}
                    className="inline-flex items-center px-4 py-2 bg-blue-500 text-gray-200 text-sm font-semibold rounded-md hover:bg-blue-600 disabled:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    Submit Progress Log
                  </button>
                  <button
                    onClick={() => setStage("workOrder")}
                    className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
                  >
                    <ArrowLeftIcon className="size-4 mr-2" />
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Alert dialog */}
        <AlertDialog
          open={dialog.open}
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          confirmText="OK"
          cancelText="Close"
          onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
          onConfirm={dialog.onConfirm}
        />
      </div>
    </div>
  );
};

export default ProgressLogTab;
